import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { BookOpen, CheckCircle, Clock, Loader2, Phone, RefreshCw, Users } from 'lucide-react';
import { PlaybookModal } from './RevivableDealQueue';
import CallRecordingButton from '../CallRecordingButton';
import CallButton from '../CallButton';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISC_COLOR = { D: '#EF4444', I: '#F59E0B', S: '#10B981', C: '#3B82F6' };

const _fmtPhone = (p) => {
  const s = String(p || '').replace(/\D/g, '');
  if (s.length >= 10) return s.replace(/(\d{2,4})(\d{3})(\d{4})$/, '$1 $2 $3');
  return p || '—';
};

const _daysAgo = (isoDate) => {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  if (isNaN(d)) return null;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
};

const _ageBadge = (days) => {
  if (days == null) return { label: '—', color: '#64748B' };
  if (days <= 3) return { label: `${days}d`, color: '#10B981' };
  if (days <= 7) return { label: `${days}d`, color: '#F59E0B' };
  return { label: `${days}d`, color: '#EF4444' };
};

/**
 * Compact "Your Revivable Deals" card for the individual coaching tab.
 * Pulls the same `/analytics/deal-outcomes/revivable-queue` endpoint the
 * manager queue uses — the endpoint already agent-scopes the results
 * server-side when a non-manager role calls it, so we don't reimplement
 * the filter here.
 *
 * Each row surfaces:
 *   - who to call + when + why it's revivable
 *   - the manager's reassignment note if applicable
 *   - a one-click "Open Playbook" that generates the DISC-tuned callback
 *     script (opening line + checklist + points to cover)
 */
const MyRevivableDealsCard = ({ domainId, agentId, isSupervisor, days = 30 }) => {
  const navigate = useNavigate();
  // DEFENCE-IN-DEPTH: never trust the `isSupervisor` prop blindly.
  // If the parent screen mis-classifies the user's role (e.g. an agent
  // whose DB role is 'domain_supervisor'), we still enforce the rule
  // that ONLY roles in the actual supervisor set can see other agents'
  // revivable deals. Everyone else sees only their own — the ext-scoped
  // backend filter is the belt, this is the braces.
  const { user } = useAuth();
  const SUPERVISOR_ROLES = ['master_admin', 'manager', 'supervisor', 'domain_admin', 'domain_supervisor'];
  const trulySupervisor = !!(user && SUPERVISOR_ROLES.includes(user.role));
  const effectiveIsSupervisor = !!isSupervisor && trulySupervisor;
  const myOwnAgentId = String(user?.extension || user?.id || '');

  const [loading, setLoading] = useState(true);
  const [queue, setQueue] = useState([]);
  const [error, setError] = useState(null);
  const [playbookFor, setPlaybookFor] = useState(null);
  const [markingContacted, setMarkingContacted] = useState({}); // { call_id: true }

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (domainId) p.set('domain_id', domainId);
    p.set('days', String(days || 30));
    p.set('sort_by', 'call_date');
    return p.toString();
  }, [domainId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/analytics/deal-outcomes/revivable-queue?${qs}`);
      const all = r.data?.queue || [];
      // Supervisor viewing another agent → show only that agent's deals.
      // Non-supervisor (agent) → ALWAYS force-filter to their OWN ext/id,
      //   even if a mis-configured parent screen passed isSupervisor=true.
      let filtered;
      if (effectiveIsSupervisor && agentId) {
        filtered = all.filter(row =>
          String(row.original_agent_id || '') === String(agentId)
          || String(row.suggested_agent_id || '') === String(agentId));
      } else if (!trulySupervisor && myOwnAgentId) {
        filtered = all.filter(row =>
          String(row.original_agent_id || '') === myOwnAgentId
          || String(row.suggested_agent_id || '') === myOwnAgentId);
      } else {
        filtered = all;
      }
      setQueue(filtered);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load revivable deals.');
    } finally {
      setLoading(false);
    }
  }, [qs, isSupervisor, agentId]);

  useEffect(() => { load(); }, [load, effectiveIsSupervisor, trulySupervisor, myOwnAgentId]);

  const markContacted = useCallback(async (callId) => {
    setMarkingContacted((s) => ({ ...s, [callId]: true }));
    try {
      await axios.post(`${API}/analytics/revival/${callId}/mark-contacted`);
      // Optimistic drop from the queue — the server has stopped
      // returning it, so we mirror that here.
      setQueue((q) => q.filter((r) => r.call_id !== callId));
      toast.success('Marked as contacted — nice work.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not mark as contacted.');
    } finally {
      setMarkingContacted((s) => {
        const cp = { ...s };
        delete cp[callId];
        return cp;
      });
    }
  }, []);

  if (loading) {
    return (
      <div
        className="rounded-xl border border-purple-200 bg-purple-50/40 p-4"
        data-testid="my-revivable-deals-card-loading"
      >
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading your revivable deals…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="my-revivable-deals-card-error">
        {error}
      </div>
    );
  }

  if (queue.length === 0) {
    return (
      <div
        className="rounded-xl border border-purple-200 bg-purple-50/40 p-4"
        data-testid="my-revivable-deals-card-empty"
      >
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-purple-600" />
          <p className="text-sm font-bold text-purple-900">Your Revivable Deals</p>
          <button
            onClick={load}
            className="ml-auto text-xs text-purple-700 hover:text-purple-900 inline-flex items-center gap-1"
            data-testid="my-revivable-deals-refresh"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
        <p className="text-xs text-slate-600 mt-2 italic">
          {isSupervisor && agentId
            ? 'This agent has no revivable deals in the last 30 days — nice.'
            : 'You currently have no revivable deals to work. Nothing to chase — go take the next inbound.'}
        </p>
      </div>
    );
  }

  return (
    <div
      className="rounded-xl border border-purple-200 bg-purple-50/40 p-4"
      data-testid="my-revivable-deals-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <BookOpen className="w-4 h-4 text-purple-600" />
        <p className="text-sm font-bold text-purple-900">
          {effectiveIsSupervisor && agentId ? 'Their Revivable Deals' : 'Your Revivable Deals'}
        </p>
        <span className="text-[11px] text-purple-700 font-semibold tabular-nums">
          {queue.length} to work
        </span>
        <button
          onClick={load}
          className="ml-auto text-xs text-purple-700 hover:text-purple-900 inline-flex items-center gap-1"
          data-testid="my-revivable-deals-refresh"
          title="Refresh queue"
        >
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      <div className="space-y-2">
        {queue.slice(0, 8).map((row) => {
          const days = _daysAgo(row.call_date || row.created_at);
          const badge = _ageBadge(days);
          const disc = row.caller_disc || row.disc_profile;
          const isReassigned = !!row.reassigned_by_manager;
          const rowKey = row.call_id;
          return (
            <div
              key={rowKey}
              className="rounded-lg border border-slate-200 bg-white p-3"
              data-testid={`my-revivable-row-${rowKey}`}
            >
              <div className="flex items-center flex-wrap gap-x-3 gap-y-1.5">
                <span
                  className="inline-flex items-center gap-1 text-[11px] font-bold px-1.5 py-0.5 rounded"
                  style={{ background: `${badge.color}22`, color: badge.color }}
                  title="Days since the original call"
                >
                  <Clock className="w-3 h-3" /> {badge.label}
                </span>
                <span className="text-sm font-semibold text-slate-800 tabular-nums inline-flex items-center gap-1">
                  <Phone className="w-3.5 h-3.5 text-slate-400" />
                  {_fmtPhone(row.customer_phone)}
                </span>
                <CallButton
                  destination={row.customer_phone}
                  reason="revivable_deal"
                  context={{ revivable_deal_id: rowKey, call_id: row.call_id }}
                  size="sm"
                  label="Call"
                />
                {(row.reassigned_agent_name || row.suggested_agent_name || row.original_agent_name) && (
                  <span
                    className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-semibold"
                    title="Assigned agent for this deal"
                    data-testid={`my-revivable-assigned-${rowKey}`}
                  >
                    <Users className="w-2.5 h-2.5 text-slate-500" />
                    {row.reassigned_agent_name || row.suggested_agent_name || row.original_agent_name}
                  </span>
                )}
                {disc && (
                  <span
                    className="inline-flex items-center justify-center w-5 h-5 rounded-full text-white text-[10px] font-bold"
                    style={{ background: DISC_COLOR[disc] || '#94A3B8' }}
                    title={`Caller DISC style: ${disc}`}
                  >
                    {disc}
                  </span>
                )}
                {row.confidence_score != null && (
                  <span className="text-[11px] text-slate-500 tabular-nums">
                    conf {Math.round(row.confidence_score)}%
                  </span>
                )}
                {isReassigned && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold"
                    title="Reassigned to you by your manager"
                  >
                    <Users className="w-2.5 h-2.5" /> allocated by manager
                  </span>
                )}
                <div className="ml-auto flex items-center gap-2">
                  {row.call_id && (
                    <CallRecordingButton
                      callId={row.call_id}
                      compact
                      data-testid={`my-revivable-listen-${rowKey}`}
                    />
                  )}
                  <button
                    onClick={() => setPlaybookFor(row)}
                    className="text-[11px] font-semibold px-2.5 py-1 rounded-md bg-teal-500 hover:bg-teal-400 text-white inline-flex items-center gap-1"
                    data-testid={`my-revivable-playbook-btn-${rowKey}`}
                    title="Open the DISC-tuned callback script"
                  >
                    <BookOpen className="w-3 h-3" /> Playbook
                  </button>
                  <button
                    onClick={() => markContacted(row.call_id)}
                    disabled={!!markingContacted[row.call_id]}
                    className="text-[11px] font-semibold px-2.5 py-1 rounded-md bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-white inline-flex items-center gap-1"
                    data-testid={`my-revivable-mark-contacted-btn-${rowKey}`}
                    title="Dismiss this deal — I've already called them"
                  >
                    {markingContacted[row.call_id] ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <CheckCircle className="w-3 h-3" />
                    )}
                    Called them
                  </button>
                </div>
              </div>
              {row.gymsales_matched && (
                <span
                  className="inline-flex items-center gap-1 mt-1 text-[10px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-full px-1.5 py-0.5"
                  data-testid={`my-revivable-gymsales-chip-${rowKey}`}
                  title={row.gymsales_matched_at ? `Matched ${new Date(row.gymsales_matched_at).toLocaleString()}` : undefined}
                >
                  GymSales lead{row.gymsales_person_name ? `: ${row.gymsales_person_name}` : ' matched'}
                </span>
              )}
              {(row.reason_detected || row.recommended_next_action) && (
                <div className="mt-1.5 space-y-0.5">
                  {row.reason_detected && (
                    <p className="text-[11px] text-slate-600 line-clamp-2" data-testid={`my-revivable-reason-${rowKey}`}>
                      <span className="font-semibold text-slate-700">Why revivable:</span> {row.reason_detected}
                    </p>
                  )}
                  {row.recommended_next_action && (
                    <p className="text-[11px] text-teal-800 line-clamp-2" data-testid={`my-revivable-followup-${rowKey}`}>
                      <span className="font-semibold">Recommended follow-up:</span> {row.recommended_next_action}
                    </p>
                  )}
                </div>
              )}
              {row.manager_note && (
                <div className="mt-2 rounded-md bg-amber-50 border border-amber-200 px-2 py-1.5">
                  <p className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">
                    Manager note
                    {row.manager_email && (
                      <span className="normal-case font-normal text-amber-600 ml-1">
                        · {row.manager_email}
                      </span>
                    )}
                  </p>
                  <p className="text-[11px] text-amber-900 mt-0.5">{row.manager_note}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {queue.length > 8 && (
        <p className="text-[11px] text-slate-500 italic mt-2">
          Showing the 8 oldest —{' '}
          <button
            type="button"
            onClick={() => navigate({ search: '?tab=revivable' })}
            className="font-semibold text-teal-700 hover:text-teal-900 not-italic underline-offset-2 hover:underline"
            data-testid="my-revivable-full-queue-link"
          >
            see the full queue in the Revivable Deals tab
          </button>.
        </p>
      )}

      {playbookFor && (
        <PlaybookModal
          row={playbookFor}
          onClose={() => {
            setPlaybookFor(null);
            toast.success('Playbook closed — go make that call.');
          }}
        />
      )}
    </div>
  );
};

export default MyRevivableDealsCard;
